import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { tokenStore } from "../../lib/api";
import { fmtBRL, fmtNumber } from "../../lib/format";
import { abcSummary, classifyAbc, countNoun, groupNouns, type AbcClass, type AbcRow } from "../../lib/calculation-memory/memory-tabs";
import type { CalculationMemoryDto } from "@bi/types";
import { fetchGroupDetails } from "./memory-data";
import { DetailTable, fmtDocument } from "./MemoryDetailTable";
import { PAGE_SIZE, Pager } from "./Pager";
import { IconChevronRight, IconDownload, IconSearch } from "../icons";

const ABC_TONE: Record<AbcClass, { dot: string; text: string; band: string }> = {
  A: { dot: "bg-tm-600", text: "text-tm-700", band: "rgb(var(--tm-500) / 0.10)" },
  B: { dot: "bg-warning-500", text: "text-warning-700", band: "rgb(245 158 11 / 0.08)" },
  C: { dot: "bg-gray-400", text: "text-gray-600", band: "rgb(156 163 175 / 0.08)" },
};

/**
 * "Quem compõe": a composição do número por cliente/fornecedor/conta, em curva
 * ABC (A = os que somam os primeiros 80%, B = até 95%, C = o resto). Abrir uma
 * linha mostra os documentos daquele grupo.
 */
export function MemoryCompositionTab({
  cardKey, unit, data, periodQS, initialGroup,
}: {
  cardKey: string;
  unit: "BRL" | "number";
  data: CalculationMemoryDto;
  periodQS: string;
  initialGroup: string | null;
}) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState<string | null>(initialGroup);
  const [page, setPage] = useState(0);
  const [csvLoading, setCsvLoading] = useState(false);
  const nouns = groupNouns(data.breakdown.groupBy);
  const rows = useMemo(() => classifyAbc(data.breakdown.rows), [data.breakdown.rows]);
  const summary = abcSummary(rows);
  const fmt = (v: number) => (unit === "number" ? fmtNumber(v) : fmtBRL(v));
  const sumAll = rows.reduce((s, r) => s + r.total, 0);

  const norm = (s: string) => s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
  const visible = q.trim()
    ? rows.filter((r) => norm(`${r.groupLabel} ${r.document ?? ""}`).includes(norm(q.trim())))
    : rows;

  async function handleExportCsv() {
    setCsvLoading(true);
    try {
      const token = tokenStore.get();
      const res = await fetch(
        `/api/calculation-memory/${encodeURIComponent(cardKey)}/export/csv${periodQS ? `?${periodQS.slice(1)}` : ""}`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} },
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `memoria-calculo-${cardKey}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Erro ao exportar CSV:", err);
    } finally {
      setCsvLoading(false);
    }
  }

  if (rows.length === 0) {
    return <div className="card p-6 text-sm text-gray-600">Nenhum {nouns.one} com movimento neste período.</div>;
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        {/* Curva de concentração */}
        <section className="card p-5 lg:col-span-3" aria-labelledby="curva-titulo">
          <h3 id="curva-titulo" className="text-sm font-semibold text-gray-900">Concentração</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            Quanto do total os maiores {nouns.many} acumulam. Quanto mais a curva sobe no começo, mais concentrado.
          </p>
          <ConcentrationCurve rows={rows} />
        </section>

        {/* Classes */}
        <section className="lg:col-span-2 grid grid-cols-3 lg:grid-cols-1 gap-3" aria-label="Classes ABC">
          {(["A", "B", "C"] as const).map((k) => (
            <div key={k} className="card px-4 py-3">
              <p className="flex items-center gap-2 text-xs font-semibold text-gray-700">
                <span className={`w-2 h-2 rounded-full ${ABC_TONE[k].dot}`} aria-hidden="true" />
                Classe {k}
                <span className="font-normal text-gray-500 hidden sm:inline">
                  {k === "A" ? "primeiros 80%" : k === "B" ? "de 80% a 95%" : "últimos 5%"}
                </span>
              </p>
              <p className="font-display font-semibold text-xl text-gray-900 tabular-nums mt-0.5">
                {fmtNumber(summary[k].count)} <span className="text-sm font-sans font-normal text-gray-600">{summary[k].count === 1 ? nouns.one : nouns.many}</span>
              </p>
              <p className="text-xs text-gray-600 tabular-nums">
                {pct(summary[k].count, rows.length)} dos {nouns.many} · {pct(summary[k].value, sumAll)} do valor
              </p>
            </div>
          ))}
        </section>
      </div>

      {/* Lista */}
      <section className="card" aria-labelledby="lista-titulo">
        <div className="px-5 py-3 border-b border-surface-border flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 id="lista-titulo" className="text-sm font-semibold text-gray-900">{fmtNumber(rows.length)} {nouns.many}</h3>
            <p className="text-xs text-gray-500">Do maior para o menor. Abra um {nouns.one} para ver os documentos.</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <label className="relative">
              <span className="sr-only">Buscar {nouns.one}</span>
              <IconSearch className="w-4 h-4 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
              <input
                className="input pl-8 py-1.5 text-sm w-64 max-w-full"
                placeholder={`Buscar ${nouns.one} ou CNPJ`}
                value={q}
                onChange={(e) => { setQ(e.target.value); setPage(0); }}
              />
            </label>
            <button
              type="button"
              className="btn-secondary text-xs py-1.5 flex items-center gap-1.5"
              onClick={handleExportCsv}
              disabled={csvLoading}
              title={`Baixa a composição por ${nouns.one}`}
            >
              <IconDownload className="w-3.5 h-3.5" />
              {csvLoading ? "Exportando…" : "Exportar CSV"}
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs text-gray-600">
              <tr className="border-b border-surface-border">
                <th className="px-4 py-2 text-left font-medium w-14">#</th>
                <th className="px-2 py-2 text-left font-medium">{nouns.title}</th>
                <th className="px-4 py-2 text-right font-medium">Valor</th>
                <th className="px-4 py-2 text-right font-medium hidden sm:table-cell">{cap(countNoun(cardKey, 2))}</th>
                <th className="px-4 py-2 text-right font-medium">%</th>
                <th className="px-4 py-2 text-right font-medium hidden md:table-cell">Acumulado</th>
                <th className="w-10" />
              </tr>
            </thead>
            <tbody className="divide-y divide-surface-border">
              {visible.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE).map((r) => (
                <CompositionRow
                  key={r.groupKey} row={r} fmt={fmt} isOpen={open === r.groupKey}
                  onToggle={() => setOpen(open === r.groupKey ? null : r.groupKey)}
                  cardKey={cardKey} periodQS={periodQS} unit={unit}
                />
              ))}
              {visible.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-6 text-center text-sm text-gray-500">Nenhum {nouns.one} encontrado para "{q}".</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <Pager page={page} total={visible.length} onPage={setPage} noun={nouns.many} />
      </section>
    </div>
  );
}

function CompositionRow({
  row: r, fmt, isOpen, onToggle, cardKey, periodQS, unit,
}: {
  row: AbcRow; fmt: (v: number) => string; isOpen: boolean; onToggle: () => void;
  cardKey: string; periodQS: string; unit: "BRL" | "number";
}) {
  const tone = ABC_TONE[r.abc];
  return (
    <>
      <tr className="hover:bg-gray-50 cursor-pointer" onClick={onToggle} aria-expanded={isOpen}>
        <td className="px-4 py-2.5 text-gray-500 tabular-nums">
          <span className="inline-flex items-center gap-1.5">
            <span className={`w-2 h-2 rounded-full ${tone.dot}`} title={`Classe ${r.abc}`} aria-label={`Classe ${r.abc}`} />
            {r.rank}
          </span>
        </td>
        <td className="px-2 py-2.5 max-w-xs">
          <p className="font-medium text-gray-900 truncate" title={r.groupLabel}>{r.groupLabel}</p>
          {r.document && <p className="text-xs text-gray-500 font-mono">{fmtDocument(r.document)}</p>}
        </td>
        <td className="px-4 py-2.5 text-right font-semibold text-gray-900 tabular-nums">{fmt(r.total)}</td>
        <td className="px-4 py-2.5 text-right text-gray-600 tabular-nums hidden sm:table-cell">{fmtNumber(r.recordCount)}</td>
        <td className="px-4 py-2.5 text-right text-gray-700 tabular-nums">
          <span className="inline-flex items-center justify-end gap-2">
            <span className="hidden sm:block w-14 h-1.5 rounded-full bg-gray-100" aria-hidden="true">
              <span className={`block h-full rounded-full ${tone.dot}`} style={{ width: `${Math.min(100, r.share)}%` }} />
            </span>
            <span className="w-12">{r.share.toFixed(1).replace(".", ",")}%</span>
          </span>
        </td>
        <td className="px-4 py-2.5 text-right text-gray-500 tabular-nums hidden md:table-cell">{r.cumulative.toFixed(1).replace(".", ",")}%</td>
        <td className="px-4 py-2.5 text-gray-400">
          <IconChevronRight className={`w-4 h-4 transition-transform ${isOpen ? "rotate-90" : ""}`} />
        </td>
      </tr>
      {isOpen && (
        <tr>
          <td colSpan={7} className="bg-gray-50 px-6 py-3">
            <GroupDocuments cardKey={cardKey} groupKey={r.groupKey} periodQS={periodQS} unit={unit} />
          </td>
        </tr>
      )}
    </>
  );
}

const DETAIL_LIMIT = 50;

function GroupDocuments({ cardKey, groupKey, periodQS, unit }: { cardKey: string; groupKey: string; periodQS: string; unit: "BRL" | "number" }) {
  const [page, setPage] = useState(0);
  const { data, isError } = useQuery({
    queryKey: ["calculation-memory-detail", cardKey, groupKey, page, periodQS],
    queryFn: () => fetchGroupDetails(cardKey, groupKey, periodQS, DETAIL_LIMIT, page * DETAIL_LIMIT),
    staleTime: 60_000,
  });
  if (isError) return <p className="text-xs text-gray-600 py-2">Não foi possível carregar os documentos deste grupo.</p>;
  return (
    <DetailTable
      data={data}
      groupKey={groupKey}
      detailPage={{ [groupKey]: page }}
      setDetailPage={(fn) => {
        const next = typeof fn === "function" ? fn({ [groupKey]: page }) : fn;
        setPage(next[groupKey] ?? 0);
      }}
      limit={DETAIL_LIMIT}
      displayUnit={unit}
    />
  );
}

/** Curva acumulada (Pareto) desenhada em SVG, com as faixas A/B/C. */
function ConcentrationCurve({ rows }: { rows: AbcRow[] }) {
  const W = 600, H = 200;
  const n = rows.length;
  const pts = [[0, H], ...rows.map((r, i) => [((i + 1) / n) * W, H - (r.cumulative / 100) * H])] as Array<[number, number]>;
  const path = pts.map(([x, y], i) => `${i ? "L" : "M"}${x.toFixed(1)},${y.toFixed(1)}`).join(" ");
  const lastA = rows.filter((r) => r.abc === "A").length;
  const lastB = lastA + rows.filter((r) => r.abc === "B").length;
  const xA = (lastA / n) * W;
  const xB = (lastB / n) * W;
  const shareA = lastA ? rows[lastA - 1]!.cumulative : 0;

  return (
    <figure className="mt-3">
      <div className="relative h-48">
        <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="absolute inset-0 w-full h-full overflow-visible" aria-hidden="true">
          <rect x="0" y="0" width={xA} height={H} fill={ABC_TONE.A.band} />
          <rect x={xA} y="0" width={xB - xA} height={H} fill={ABC_TONE.B.band} />
          <rect x={xB} y="0" width={W - xB} height={H} fill={ABC_TONE.C.band} />
          {[80, 95].map((p) => (
            <line key={p} x1="0" x2={W} y1={H - (p / 100) * H} y2={H - (p / 100) * H} stroke="rgb(156 163 175)" strokeDasharray="4 4" strokeWidth="1" vectorEffect="non-scaling-stroke" />
          ))}
          <path d={`${path} L${W},${H} Z`} fill="rgb(var(--tm-400) / 0.12)" />
          <path d={path} fill="none" stroke="rgb(var(--tm-600))" strokeWidth="2.5" vectorEffect="non-scaling-stroke" strokeLinejoin="round" />
        </svg>
        <span className="absolute left-1.5 text-[11px] text-gray-500 -translate-y-full" style={{ top: "20%" }}>80%</span>
        <span className="absolute left-1.5 text-[11px] text-gray-500 -translate-y-full" style={{ top: "5%" }}>95%</span>
      </div>
      <figcaption className="mt-2 text-xs text-gray-600">
        Os primeiros <strong className="text-gray-900">{fmtNumber(lastA)}</strong> de {fmtNumber(n)} ({pct(lastA, n)}) somam{" "}
        <strong className="text-gray-900">{shareA.toFixed(1).replace(".", ",")}%</strong> do valor.
      </figcaption>
    </figure>
  );
}

function pct(part: number, total: number) {
  return total ? `${((part / total) * 100).toFixed(1).replace(".", ",")}%` : "0%";
}

function cap(s: string) { return s.charAt(0).toUpperCase() + s.slice(1); }
